import { useRouter } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

/**
 * Hook to track page views with PostHog when routes change.
 * Should be used once at the router level.
 */
export function useTrackPageView(): void {
  const router = useRouter();
  const posthog = usePostHog();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Track initial page view
    const initialPath = router.state.location.pathname;
    if (posthog && initialPath !== lastPathRef.current) {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
      });
      lastPathRef.current = initialPath;
    }

    // Subscribe to route changes
    const unsubscribe = router.subscribe("onResolved", ({ toLocation }) => {
      if (!posthog) {
        return;
      }

      const newPath = toLocation.pathname;
      if (newPath !== lastPathRef.current) {
        posthog.capture("$pageview", {
          $current_url: window.location.origin + newPath + toLocation.search,
        });
        lastPathRef.current = newPath;
      }
    });

    return unsubscribe;
  }, [router, posthog]);
}
